import { action, computed, observable } from "mobx";
import { observer, Provider } from "mobx-react";
import * as React from "react";
import { Route, RouteComponentProps } from "react-router-dom";
import { AddButton } from "../Buttons/AddButton";
import { DragAndDropButton } from "../Buttons/DragAndDropButton";
import { Area } from "../General/Area";
import { AreaAside } from "../General/AreaAside";
import { Link } from "../General/Link";
import { Toolbar } from "../General/Toolbar";
import { TypeAheadSearchInput } from "../General/TypeAheadSearchInput";
import * as MetaManagement from "../MetaManagement";
import { AppStore } from "../Stores/AppStore";
import { DialogResult, DialogStore } from "../Stores/DialogStore";
import { MetaDataStore } from "../Stores/MetaDataStore";
import { NotificationStore } from "../Stores/NotificationStore";
import { Strings } from "../Stores/StringsStore";
import { Urls } from "../Urls";
import { newGuid } from "../Utilities";
import { MetaEdit } from "./MetaEdit";
import { GlobalMetaIcon } from "../Icons/GlobalMetaIcon";


interface IMetaManagementRouteProps extends RouteComponentProps<{}> { }

@observer
export class Metas extends React.Component<IMetaManagementRouteProps, {}> {

	store: MetaDataStore = new MetaDataStore();
	@observable tempModel: MetaManagement.MetaInfo[] = [];
	wasCollapsed: boolean;

	componentDidMount() {
		this.store.search();
		this.store.loadUiSettings();
		this.setEditMode();
		this.setTitle();
		window.addEventListener('orientationchange', this.handleOrientation);
	}

	componentDidUpdate() {
		this.setEditMode();
		this.setTitle();
	}

	componentWillUnmount() {
		window.removeEventListener("orientationchange", this.handleOrientation);
	}

	@action setEditMode = () => {
		this.store.isEditMode = this.store.model.some(item => item.guid === this.store.currentFocusedItem);
	}

	setTitle = () => {
		if (!this.store.currentFocusedItem) {
			AppStore.setTitle(Strings.appNavigation.metaDataLinkText);
		}
		else if (this.store.isEditMode) {
			AppStore.setTitle(Strings.metaManagement.metaEdit.title);
		}
		else {
			AppStore.setTitle(Strings.metaManagement.metaEdit.addMetaTitle);
		}
	}

	addButtonClick = () => {
		this.store.isEditMode = false;
		AppStore.keepOnPage(Urls.metas + "/" + newGuid(), true);
		this.store.areaUiInfo.selectCollapse();
	}

	moveButtonClick = () => {
		this.store.moveMode = !this.store.moveMode;
		if (this.store.moveMode) {
			this.tempModel = this.store.model.slice();
		}
		//We have to store this information for locking the AreaAside slider drag event.
		AppStore.metaComponentIsInMoveMode = this.store.moveMode;
		if (!this.store.moveMode && this.store.hash != JSON.stringify(this.store.model)) {
			DialogStore.show(Strings.metaManagement.metas.saveSortOrderTitle, Strings.metaManagement.metas.saveSortOrderText).then(answer => {
				if (answer == DialogResult.Yes) {
					// get guids by new sort oder 
					let guids: string[] = [];
					for (let i = 0; i < this.store.model.length; i++) {
						guids.push(this.store.model[i].guid);
					}

					// save new sort order
					MetaManagement.reorderMetasAsync(guids).then(model => {
						this.store.model = model;
						this.store.hash = JSON.stringify(this.store.model);
						NotificationStore.showSuccessMessage(Strings.general.saveSuccessMessage);
					}).catch(error => {
						DialogStore.showError(Strings.general.errorDialogTitle, error);
					});
				}
				else {
					this.store.model = this.tempModel;
				}
				this.tempModel = [];
			});
		}
	}

	@computed get canMove(): boolean {
		return this.store.searchTerm == "";
	}

	handleDragStart = (event: React.DragEvent<any>) => {
		let elem = (event.target as HTMLLIElement);
		this.store.moveFrom = Number(elem.getAttribute("data-id")) + 2;

		// this is needed for Firefox
		event.dataTransfer.setData("text", "dummy");

		// Only move is allowed
		event.dataTransfer.effectAllowed = "move";
	}

	handleDragOver = (event: React.DragEvent<any>) => {
		event.preventDefault();
	}

	handleDragEnter = (event: React.DragEvent<any>) => {
		event.preventDefault();
		event.dataTransfer.effectAllowed = "move";
	}

	handleDrop = (event: React.DragEvent<any>) => {
		event.preventDefault();
		let elem = (event.target as HTMLLIElement);

		let moveTo = Number(elem.getAttribute("data-id")) + 2;
		let meta = this.store.model.splice(this.store.moveFrom, 1)[0];
		this.store.model.splice(moveTo, 0, meta);
	}

	handleSearchTermChangeFromTypeAhead = (searchTerm: string) => {
		this.store.searchTerm = searchTerm;
		clearTimeout(this.store.searchTimeoutHandle);
		this.store.searchTimeoutHandle = setTimeout(() => this.store.search(), 300);
	}

	handleOrientation = () => {
		if (screen.width > screen.height) {
			if (screen.width >= 880 && this.store.areaUiInfo.isCollapsed) {
				this.store.areaUiInfo.isCollapsed = false;
				this.wasCollapsed = true;
			}
		}
		else if (!this.store.areaUiInfo.isCollapsed && this.wasCollapsed) {
			this.store.areaUiInfo.isCollapsed = true;
			this.wasCollapsed = false;
		}
	}

	metaClassName(item: MetaManagement.MetaInfo) {
		let className = "";
		if (this.store.currentFocusedItem === item.guid) {
			className += "is--item-open";
		}
		if (item.isGlobal) {
			className += className ? " is--global" : "is--global";
		}
		return className;
	}

	render() {
		let noCollapse = this.props.children === null || !this.store.currentFocusedItem;
		let aside = <AreaAside noCollapse={noCollapse}>
			<Toolbar>
				<TypeAheadSearchInput showSuggestionList={false} searchTermChangeCallback={this.handleSearchTermChangeFromTypeAhead} buttonAlignment={2} />
				<AddButton title={Strings.metaManagement.metas.addButtonTooltip} onClick={this.addButtonClick} disabled={this.store.moveMode} testId="addButton" />
				<DragAndDropButton title={Strings.metaManagement.metas.moveButtonTooltip} onClick={this.moveButtonClick} disabled={!this.canMove} pressed={this.store.moveMode} testId="moveButton" />
			</Toolbar>

			<div className="area-grid__body-content">
				<h1>{this.store.isEditMode ? Strings.metaManagement.metaEdit.title : Strings.metaManagement.metaEdit.addMetaTitle}</h1>
				<div className="list-simple">
					<ul data-test-id="itemList-global" className="global">
						{this.store.model.filter(m => m.isGlobal).map((item, index) => {
							return <li key={item.guid} className={this.metaClassName(item)}>
								<Link to={Urls.metas + "/" + item.guid} action={this.store.areaUiInfo.selectCollapse}>{item.caption}
									<span className="list-simple__additional"><GlobalMetaIcon title={Strings.metaManagement.metas.predefinedMetaIconTooltip} /></span>
								</Link>
							</li>
						})}
					</ul>
					<ul data-test-id="itemList">
						{this.store.model.filter(m => !m.isGlobal).map((item, index) => {
							return <li key={item.guid} className={this.metaClassName(item)}>
								{this.store.moveMode ? <span data-id={index} draggable={true} onDragStart={this.handleDragStart} onDragOver={this.handleDragOver} onDragEnter={this.handleDragEnter} onDrop={this.handleDrop} className="draggable-list-simple">{item.caption}</span> : null}
								{!this.store.moveMode ? <Link to={Urls.metas + "/" + item.guid} action={this.store.areaUiInfo.selectCollapse}>{item.caption}</Link> : null}
							</li>
						})}
					</ul>
				</div>
			</div>
		</AreaAside>;
		return <Provider store={this.store} areaUiInfo={this.store.areaUiInfo}>
			<Area asideComponent={aside}>
				<Route path={`${this.props.match.url}/:id`} component={MetaEdit} />
			</Area>
		</Provider>;
	}
}
